import React from 'react';
import css from './Breadcrumb.css';
import classNames from 'classnames';
import IconSpinner from '../IconSpinner/IconSpinner';

const Breadcrumb = ({ paths, urlPrefix, rootClassName, loading }) => {
    if (loading) {
        return (
            <div className={classNames(css.breadcrumb, rootClassName)}>
                <IconSpinner />
            </div>
        );
    }

    const KNOWN_REGIONS = {
        'kanton-aargau': 'Kanton Aargau',
        'kanton-bern': 'Kanton Bern',
        'kanton-freiburg': 'Kanton Freiburg',
        'kanton-genf': 'Kanton Genf',
        'kanton-glarus': 'Kanton Glarus',
        'kanton-graubuenden': 'Kanton Graubünden',
        'kanton-jura': 'Kanton Jura',
        'kanton-luzern': 'Kanton Luzern',
        'kanton-neunenburg': 'Kanton Neunenburg',
        'kanton-st-gallen': 'Kanton St. Gallen',
        'kanton-schaffhausen': 'Kanton Schaffhausen',
        'kanton-schwyz': 'Kanton Schwyz',
        'kanton-solothurn': 'Kanton Solothurn',
        'kanton-thurgau': 'Kanton Thurgau',
        'kanton-tessin': 'Kanton Tessin',
        'kanton-uri': 'Kanton Uri',
        'kanton-wallis': 'Kanton Wallis',
        'kanton-waadt': 'Kanton Waadt',
        'kanton-zug': 'Kanton Zug',
        'kanton-zuerich': 'Kanton Zürich',
        'kanton-appenzell-ausserrhoden': 'Kanton Appenzell Ausserrhoden',
        'kanton-appenzell-innerrhoden': 'Kanton Appenzell Innerrhoden',
        'kanton-basel-stadt': 'Kanton Basel-Stadt',
        'kanton-basel-landschaft': 'Kanton Basel-Landschaft',
        'kanton-obwalden': 'Kanton Obwalden',
        'kanton-nidwalden': 'Kanton Nidwalden',
        bayern: 'Bayern',
        niedersachsen: 'Niedersachsen',
        'baden-wuerttemberg': 'Baden-Württemberg',
        'nordrhein-westfalen': 'Nordrhein-Westfalen',
        brandenburg: 'Brandenburg',
        'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
        hessen: 'Hessen',
        'sachsen-anhalt': 'Sachsen-Anhalt',
        'rheinland-pfalz': 'Rheinland-Pfalz',
        sachsen: 'Sachsen',
        thueringen: 'Thüringen',
        'schleswig-holstein': 'Schleswig-Holstein',
        saarland: 'Saarland',
        berlin: 'Berlin',
        hamburg: 'Hamburg',
        bremen: 'Bremen',
    };

    const replaceUmlauts = str => {
        return str
            .replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/ß/g, 'ss');
    };

    const formatPathLabel = (paths, index) => {
        return paths
            .slice(0, index + 1)
            .map(path => {
                return path.urlPath
                    ? encodeURIComponent(path.urlPath)
                    : encodeURIComponent(
                          replaceUmlauts(path.label.toLowerCase()).replace(/ /g, '-')
                      );
            })
            .join('/');
    };

    // correct canton labels
    const correctLabel = label => {
        const corrected = KNOWN_REGIONS[label];
        return corrected ? corrected : label;
    };

    const baseUrl = process.env.REACT_APP_CANONICAL_ROOT_URL || '';

    const normalizedUrlPrefix = urlPrefix?.startsWith('/') ? urlPrefix : `/${urlPrefix}`;

    const renderItemContent = ({ url, label, noLink }, index) => {
        const text = correctLabel(label);

        if (noLink) {
            return <span className={css.pseudoLink}>{text}</span>;
        }
        if (url) {
            return (
                <a href={url} className={css.breadcrumbItemLink}>
                    {text}
                </a>
            );
        }
        if (urlPrefix) {
            return (
                <a
                    href={`${normalizedUrlPrefix}/${formatPathLabel(paths, index)}`}
                    className={css.breadcrumbItemLink}
                >
                    {text}
                </a>
            );
        }
        return <span>{text}</span>;
    };

    const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: paths.map(({ label, url }, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: correctLabel(label),
            item: url || `${baseUrl}${normalizedUrlPrefix}/${formatPathLabel(paths, index)}`,
        })),
    };

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
            />

            <div className={classNames(css.breadcrumb, rootClassName)} aria-label="breadcrumb">
                {paths.map((path, index) => (
                    <React.Fragment key={index}>
                        {index > 0 && (
                            <svg
                                className={css.breadcrumbSeparator}
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M5.158 2.408 C 4.978 2.458,4.789 2.642,4.738 2.818 C 4.712 2.910,4.745 3.060,4.813 3.160 C 4.842 3.202,5.647 4.079,6.603 5.108 C 7.559 6.138,8.342 6.989,8.342 7.000 C 8.342 7.011,7.559 7.862,6.603 8.892 C 5.647 9.921,4.842 10.798,4.813 10.840 C 4.662 11.061,4.728 11.329,4.976 11.499 C 5.172 11.634,5.338 11.638,5.511 11.511 C 5.560 11.475,6.380 10.604,7.333 9.577 C 9.408 7.338,9.276 7.485,9.353 7.339 C 9.407 7.236,9.415 7.195,9.415 7.000 C 9.415 6.804,9.407 6.764,9.351 6.658 C 9.278 6.519,9.314 6.558,7.191 4.270 C 6.231 3.235,5.538 2.507,5.478 2.470 C 5.359 2.396,5.266 2.378,5.158 2.408 "
                                    stroke="none"
                                    fillRule="evenodd"
                                    fill="#1A2B49"
                                />
                            </svg>
                        )}
                        <li className={css.breadcrumbItem}>{renderItemContent(path, index)}</li>
                    </React.Fragment>
                ))}
            </div>
        </>
    );
};

export default Breadcrumb;
